import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SensorsIcon from '@mui/icons-material/Sensors';
import PentagonIcon from '@mui/icons-material/Pentagon';
import { geofenceService, Geofence } from '@signals/app-shell/services/geofenceService';
import pathfinderApi, { AdtechAreaSearchResult } from '../../services/pathfinderApi';
import type { AdtechMapSignal } from './AdtechResultsTable';
import AdtechDeviceActions from './AdtechDeviceActions';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';

interface GeofencePanelProps {
  dateRange: { start: Date | null; end: Date | null };
  queryTimeZone?: string;
  adtechEnabled: boolean;
  /** Optional course scope for Saved Devices / watchlist. */
  courseId?: string;
  /** Load full pattern-of-life for AdTech registration ids. */
  onLoadAdtechPol?: (registrationIds: string[], label?: string) => void;
  adtechPolLoading?: boolean;
  geofences: Geofence[];
  loadingGeofences?: boolean;
  selectedGeofenceId: number | null;
  onSelectGeofence: (id: number | null) => void;
  isDrawing: boolean;
  onStartDrawing: () => void;
  onStopDrawing: () => void;
  /** GeoJSON Polygon geometry emitted by the map after a draw completes. */
  pendingGeofencePolygon: any | null;
  onClearPending: () => void;
  /** Ask the parent to reload the geofence list. */
  onGeofencesChanged: () => void;
  onZoomTo?: (lat: number, lng: number) => void;
  /** Push geofence AdTech hits onto the shared map layer + results table. */
  onLoadAdtechToMap?: (signals: AdtechMapSignal[], label?: string) => void;
}

/** Normalize API device rows into map-layer signals (lat/lon required). */
function toMapSignals(devices: Array<Record<string, any>> | undefined): AdtechMapSignal[] {
  if (!devices?.length) return [];
  return devices
    .map((d) => {
      const latitude = Number(d?.latitude ?? d?.lat);
      const longitude = Number(d?.longitude ?? d?.lon);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      return {
        registration_id: String(d.registration_id || d.registrationID || 'unknown'),
        latitude,
        longitude,
        timestamp: d.timestamp ?? null,
        carrier: d.carrier ?? null,
        country: d.country ?? null,
        accuracy: d.accuracy ?? null,
        ip_address: d.ip_address ?? d.ipAddress ?? null,
        timezone: d.timezone ?? null,
        forensic_flag: d.forensic_flag ?? d.forensicFlag ?? null,
      } as AdtechMapSignal;
    })
    .filter((s): s is AdtechMapSignal => s != null);
}

// Ring centroid for zoom-to.
function ringCentroid(ring: number[][]): [number, number] | null {
  if (!ring || ring.length === 0) return null;
  let sx = 0;
  let sy = 0;
  ring.forEach(([lng, lat]) => {
    sx += lng;
    sy += lat;
  });
  return [sy / ring.length, sx / ring.length]; // [lat, lng]
}

const GeofencePanel: React.FC<GeofencePanelProps> = ({
  dateRange,
  queryTimeZone,
  adtechEnabled,
  geofences,
  loadingGeofences = false,
  selectedGeofenceId,
  onSelectGeofence,
  isDrawing,
  onStartDrawing,
  onStopDrawing,
  pendingGeofencePolygon,
  onClearPending,
  onGeofencesChanged,
  onZoomTo,
  onLoadAdtechToMap,
  courseId,
  onLoadAdtechPol,
  adtechPolLoading = false,
}) => {
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
  // Create dialog
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // AdTech query
  const [mode, setMode] = useState<'inside' | 'near'>('inside');
  const [bufferMeters, setBufferMeters] = useState(250);
  const [querying, setQuerying] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [result, setResult] = useState<AdtechAreaSearchResult | null>(null);

  // Open the name dialog whenever the map hands us a freshly drawn polygon.
  useEffect(() => {
    if (pendingGeofencePolygon) {
      setNewName('');
      setNewDescription('');
      setSaveError(null);
      setNameDialogOpen(true);
    }
  }, [pendingGeofencePolygon]);

  const selectedGeofence = useMemo(
    () => geofences.find((g) => g.id === selectedGeofenceId) || null,
    [geofences, selectedGeofenceId],
  );

  // Reset AdTech results when the selection changes.
  useEffect(() => {
    setResult(null);
    setQueryError(null);
  }, [selectedGeofenceId]);

  const handleCancelDraw = useCallback(() => {
    onStopDrawing();
  }, [onStopDrawing]);

  const handleSaveGeofence = useCallback(async () => {
    if (!pendingGeofencePolygon || !newName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      // pendingGeofencePolygon is a GeoJSON Polygon geometry ([[[lng, lat], ...]]).
      await geofenceService.createGeofence({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        geometry: {
          type: 'Polygon',
          coordinates: pendingGeofencePolygon.coordinates,
        },
      });
      setNameDialogOpen(false);
      onClearPending();
      onStopDrawing();
      onGeofencesChanged();
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save geofence');
    } finally {
      setSaving(false);
    }
  }, [pendingGeofencePolygon, newName, newDescription, onClearPending, onStopDrawing, onGeofencesChanged]);

  const handleCancelSave = useCallback(() => {
    setNameDialogOpen(false);
    onClearPending();
    onStopDrawing();
  }, [onClearPending, onStopDrawing]);

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await geofenceService.deleteGeofence(id);
        if (selectedGeofenceId === id) onSelectGeofence(null);
        onGeofencesChanged();
      } catch (err) {
        console.error('[GeofencePanel] delete failed', err);
      }
    },
    [selectedGeofenceId, onSelectGeofence, onGeofencesChanged],
  );

  const handleQueryAdtech = useCallback(async () => {
    if (!selectedGeofence) return;
    const ring = selectedGeofence.geometry?.coordinates?.[0];
    if (!ring || ring.length < 3) {
      setQueryError('Selected geofence has no valid polygon.');
      return;
    }
    if (!dateRange.start || !dateRange.end) {
      setQueryError('Select a date range first.');
      return;
    }
    setQuerying(true);
    setQueryError(null);
    setResult(null);
    try {
      const res = await pathfinderApi.queryAdtechGeofenceSearch({
        polygon: ring as Array<[number, number]>,
        dateRange: [dateRange.start, dateRange.end],
        bufferMeters: mode === 'near' ? bufferMeters : 0,
        timeZone: queryTimeZone,
      });
      setResult(res);
      // Same path Area Search uses — sidebar alone is not enough for map/table/toolbar.
      const mappable = toMapSignals(res.devices);
      if (onLoadAdtechToMap && mappable.length > 0) {
        const where = mode === 'near' ? `near ${bufferMeters}m` : 'inside';
        onLoadAdtechToMap(
          mappable,
          `Geofence: ${selectedGeofence.name} (${where}, ${mappable.length} signals)`,
        );
      }
    } catch (err: any) {
      setQueryError(err?.message || 'AdTech query failed');
    } finally {
      setQuerying(false);
    }
  }, [selectedGeofence, dateRange, mode, bufferMeters, queryTimeZone, onLoadAdtechToMap]);

  // Group AdTech signals by registration_id for a device-level list.
  const deviceRows = useMemo(() => {
    if (!result?.devices?.length) return [];
    const byDevice = new Map<string, { count: number; last?: string; carrier?: string; country?: string }>();
    result.devices.forEach((d: any) => {
      const rid = d.registration_id || 'unknown';
      const entry = byDevice.get(rid) || { count: 0 };
      entry.count += 1;
      if (!entry.last || (d.timestamp && d.timestamp > entry.last)) entry.last = d.timestamp;
      entry.carrier = entry.carrier || d.carrier;
      entry.country = entry.country || d.country;
      byDevice.set(rid, entry);
    });
    return Array.from(byDevice.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 200);
  }, [result]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PentagonIcon sx={{ fontSize: 18, color: '#00bcd4' }} /> Geofences
        </Typography>
        <Button
          size="small"
          variant={isDrawing ? 'contained' : 'outlined'}
          color={isDrawing ? 'warning' : 'primary'}
          startIcon={<AddLocationAltIcon />}
          onClick={isDrawing ? handleCancelDraw : onStartDrawing}
          sx={{ fontSize: '0.7rem' }}
        >
          {isDrawing ? 'Cancel' : 'Draw Geofence'}
        </Button>
      </Box>

      {isDrawing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Click on the map to add polygon vertices; double-click to finish.
        </Typography>
      )}

      {loadingGeofences ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : geofences.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No geofences yet. Draw one to get started.
        </Typography>
      ) : (
        <List dense sx={{ maxHeight: 220, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1 }}>
          {geofences.map((g) => {
            const selected = g.id === selectedGeofenceId;
            return (
              <ListItem
                key={g.id}
                disablePadding
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Zoom to geofence">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const c = ringCentroid(g.geometry?.coordinates?.[0] || []);
                          if (c && onZoomTo) onZoomTo(c[0], c[1]);
                        }}
                      >
                        <MyLocationIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {intelEnabled && (
                      <Tooltip title="Promote to Intel Packet">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPromoteTarget({
                              member_type: 'geofence',
                              member_id: String(g.id),
                              label: g.name,
                              is_primary: true,
                            });
                          }}
                        >
                          <FactCheckIcon fontSize="small" sx={{ color: '#ffb300' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete geofence">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(g.id);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => onSelectGeofence(selected ? null : g.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={g.name}
                    secondary={g.description || undefined}
                    primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 500 }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* AdTech query for the selected geofence */}
      {selectedGeofence && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <SensorsIcon sx={{ fontSize: 16, color: '#e91e63' }} /> AdTech in "{selectedGeofence.name}"
          </Typography>

          {!adtechEnabled ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              AdTech module is not available. Enable it in Settings and ensure the server is configured.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={mode}
                  onChange={(_, v) => { if (v) setMode(v); }}
                >
                  <ToggleButton value="inside" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
                    Inside
                  </ToggleButton>
                  <ToggleButton value="near" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
                    Near
                  </ToggleButton>
                </ToggleButtonGroup>
                {mode === 'near' && (
                  <TextField
                    size="small"
                    type="number"
                    label="Buffer (m)"
                    value={bufferMeters}
                    onChange={(e) => setBufferMeters(Math.max(0, Number(e.target.value) || 0))}
                    sx={{ width: 110 }}
                    inputProps={{ min: 0, step: 50 }}
                  />
                )}
              </Box>

              <Button
                variant="contained"
                size="small"
                onClick={handleQueryAdtech}
                disabled={querying || !dateRange.start || !dateRange.end}
                startIcon={querying ? <CircularProgress size={14} color="inherit" /> : <SensorsIcon />}
                sx={{ fontSize: '0.7rem', bgcolor: '#e91e63', '&:hover': { bgcolor: '#c2185b' } }}
              >
                {querying ? 'Querying...' : 'Query AdTech'}
              </Button>

              {queryError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {queryError}
                </Typography>
              )}

              {result && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                    <Chip
                      label={`${result.unique_count} unique devices`}
                      size="small"
                      sx={{ bgcolor: 'rgba(233,30,99,0.15)', color: '#e91e63', fontWeight: 'bold' }}
                    />
                    <Chip label={`${result.total_signals} signals`} size="small" variant="outlined" />
                    {onLoadAdtechToMap && (() => {
                      const mappable = toMapSignals(result.devices);
                      return (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MyLocationIcon sx={{ fontSize: 16 }} />}
                          disabled={mappable.length === 0}
                          onClick={() => {
                            const where = mode === 'near' ? `near ${bufferMeters}m` : 'inside';
                            onLoadAdtechToMap(
                              mappable,
                              `Geofence: ${selectedGeofence.name} (${where}, ${mappable.length} signals)`,
                            );
                          }}
                          sx={{ fontSize: '0.7rem', borderColor: '#e91e63', color: '#e91e63' }}
                        >
                          Load {mappable.length} to map
                        </Button>
                      );
                    })()}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Commercial mobile-location observations {mode === 'near' ? `within ${bufferMeters}m of` : 'inside'} this geofence. Not persisted.
                  </Typography>
                  {deviceRows.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No devices found for this window.
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {deviceRows.map(([rid, info]) => (
                        <Box
                          key={rid}
                          sx={{
                            py: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', wordBreak: 'break-all' }}>
                              {rid}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {info.count} obs{info.carrier ? ` · ${info.carrier}` : ''}{info.country ? ` · ${info.country}` : ''}
                            </Typography>
                          </Box>
                          <AdtechDeviceActions
                            registrationId={rid}
                            courseId={courseId}
                            onLoadPol={onLoadAdtechPol}
                            polLoading={adtechPolLoading}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Name / save dialog after drawing */}
      <Dialog open={nameDialogOpen} onClose={handleCancelSave} maxWidth="xs" fullWidth>
        <DialogTitle>Save Geofence</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
          {saveError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {saveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveGeofence} variant="contained" disabled={saving || !newName.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        defaultName={promoteTarget?.label ? `Geofence: ${promoteTarget.label}` : undefined}
      />
    </Box>
  );
};

export default GeofencePanel;
